import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ShieldIcon, TargetIcon, UsersIcon } from 'lucide-react';
export function ReadinessDefinitionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-slate-900 py-24 lg:py-32" data-id="element-234">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-235">
        {/* Header */}
        <motion.div className="text-center max-w-3xl mx-auto mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-236">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight mb-6" data-id="element-237">
            Readiness Means One Simple Thing:
          </h2>

          <div className="relative" data-id="element-238">
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-cyan-500/30 rounded-full" data-id="element-239" />
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed pl-6 italic" data-id="element-240">
              Do we have the right people, with the right skills, in the right
              conditions — ready to execute right now?
            </p>
          </div>
        </motion.div>

        {/* Three readiness type cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-241">
          <motion.div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 group hover:bg-slate-800 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:border-white/20 transition-all duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-242">
            <div className="w-12 h-12 rounded-xl bg-tiq-purple/10 border border-tiq-purple/20 flex items-center justify-center mb-5 group-hover:bg-tiq-purple/20 group-hover:border-tiq-purple/40 transition-colors" data-id="element-243">
              <ShieldIcon className="w-6 h-6 text-tiq-purple" data-id="element-244" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-245">
              Operational Readiness
            </h3>
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-3 group-hover:text-slate-400" data-id="element-246">
              OPS-R
            </p>
            <p className="text-slate-300 group-hover:text-white transition-colors" data-id="element-247">
              Can the team execute today?
            </p>
          </motion.div>

          <motion.div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 group hover:bg-slate-800 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:border-white/20 transition-all duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.45
        }} data-id="element-248">
            <div className="w-12 h-12 rounded-xl bg-tiq-green/10 border border-tiq-green/20 flex items-center justify-center mb-5 group-hover:bg-tiq-green/20 group-hover:border-tiq-green/40 transition-colors" data-id="element-249">
              <TargetIcon className="w-6 h-6 text-tiq-green" data-id="element-250" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-251">
              Mission Readiness
            </h3>
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-3 group-hover:text-slate-400" data-id="element-252">
              MSN-R
            </p>
            <p className="text-slate-300 group-hover:text-white transition-colors" data-id="element-253">
              Are they prepared for the specific environment?
            </p>
          </motion.div>

          <motion.div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 group hover:bg-slate-800 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:border-white/20 transition-all duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} data-id="element-254">
            <div className="w-12 h-12 rounded-xl bg-tiq-blue/10 border border-tiq-blue/20 flex items-center justify-center mb-5 group-hover:bg-tiq-blue/20 group-hover:border-tiq-blue/40 transition-colors" data-id="element-255">
              <UsersIcon className="w-6 h-6 text-tiq-blue" data-id="element-256" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-257">
              Human Readiness
            </h3>
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-3 group-hover:text-slate-400" data-id="element-258">
              HMN-R
            </p>
            <p className="text-slate-300 group-hover:text-white transition-colors" data-id="element-259">
              Are fatigue, stress, and capacity aligned?
            </p>
          </motion.div>
        </div>

        {/* Closing */}
        <motion.p className="text-center text-lg text-slate-400 max-w-2xl mx-auto" initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.8
      }} data-id="element-260">
          Because capability on paper doesn't always equal performance in
          reality.
        </motion.p>
      </div>
    </section>;
}